import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { scheduleDowngradesApi } from "@/react/api/scheduleDowngradesApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

interface AccountSubscription {
  id: number;
}

interface ScheduleDowngradeSuccessResponse {
  data: AccountSubscription;
}

interface CancelDowngradeSuccessResponse {
  success: true;
}

interface ScheduleDowngradeErrorResponse {
  error_details?: unknown;
  message: string;
  success: false;
}

interface UseScheduleDowngradesApiOptions {
  onScheduleDowngradeSuccess?: () => void;
}

export const useScheduleDowngradesApi = ({
  onScheduleDowngradeSuccess,
}: UseScheduleDowngradesApiOptions = {}) => {
  const { success, error } = useToast();
  const { mutate: scheduleDowngrade, isPending: isSchedulingDowngrade } =
    useMutation<
      ScheduleDowngradeSuccessResponse,
      ScheduleDowngradeErrorResponse,
      number
    >({
      mutationFn: (planId: number) => scheduleDowngradesApi.create(planId),
      onSuccess: () => {
        onScheduleDowngradeSuccess?.();
        success(t("pricing_plans.subscription_updated"));
      },
      onError: () => {
        error(t("pricing_plans.errors.schedule_downgrade_error"));
      },
    });

  const { mutate: cancelScheduledDowngrade, isPending: isCancelingDowngrade } =
    useMutation<
      CancelDowngradeSuccessResponse,
      ScheduleDowngradeErrorResponse,
      void
    >({
      mutationFn: () => scheduleDowngradesApi.cancel(),
      onSuccess: () => {
        success(t("pricing_plans.downgrade_schedule_cancelation.success"));
      },
      onError: () => {
        error(t("pricing_plans.downgrade_schedule_cancelation.error"));
      },
    });

  return {
    scheduleDowngrade,
    isSchedulingDowngrade,
    cancelScheduledDowngrade,
    isCancelingDowngrade,
  };
};
